import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Edit, 
  History, 
  Assignment, 
  Person, 
  Schedule, 
  LocationOn,
  CheckCircle,
  Warning,
  PlayArrow,
  Pause,
  Stop,
  ArrowBack,
  Save,
  Cancel,
  PictureAsPdf,
  Visibility
} from '@mui/icons-material';
import { Permit, PermitCancellationRequestData, PermitTransferRequestData, PermitSuspensionRequestData, PermitTransferCompletionData } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import permitService, { ApiError } from '../../services/permitService';
import Autocomplete from '@mui/material/Autocomplete';
import authService from '../../services/authService';

type UserSearchResult = any[] | { results: any[] };

const PermitDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  
  const [permit, setPermit] = useState<Permit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [updating, setUpdating] = useState(false);
  const [missingRequirements, setMissingRequirements] = useState<any[] | null>(null);
  
  // Cancellation request dialog state
  const [cancellationDialogOpen, setCancellationDialogOpen] = useState(false);
  const [cancellationForm, setCancellationForm] = useState({
    workparty_signed_off: false,
    rasms_removed: false,
    restoration_checks_completed: false,
    plant_restrictions: '',
    plant_modifications: ''
  });
  const [requestingCancellation, setRequestingCancellation] = useState(false);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    work_description: '',
    work_location: '',
    requested_start_time: '',
    requested_end_time: '',
    isolation_required: false,
    risk_assessment_completed: false,
    equipment_removed_from_service: '',
    issuer_applied_safety_measures: '',
    work_completion_notes: '',
    equipment_modifications_made: ''
  });
  const [saving, setSaving] = useState(false);

  // Add state for transfer dialog
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    new_recipient: null as any,
    transfer_notes: '',
    workparty_signed_off: false,
    recipientOptions: [] as any[],
    recipientSearch: '',
    loadingRecipients: false,
  });
  const [requestingTransfer, setRequestingTransfer] = useState(false);

  // Add state for suspension dialog
  const [suspensionDialogOpen, setSuspensionDialogOpen] = useState(false);
  const [suspensionForm, setSuspensionForm] = useState({
    suspension_rasm_removed: false,
    suspension_workparty_signed_off: false,
    suspension_modifications_notes: '',
    suspension_wip_updated: false,
    suspension_rasm_lock_removed: false,
  });
  const [requestingSuspension, setRequestingSuspension] = useState(false);

  // Add state for complete transfer dialog
  const [completeTransferDialogOpen, setCompleteTransferDialogOpen] = useState(false);
  const [completeTransferForm, setCompleteTransferForm] = useState({
    new_recipient: null as any,
    recipientOptions: [] as any[],
    recipientSearch: '',
    loadingRecipients: false,
  });
  const [completingTransfer, setCompletingTransfer] = useState(false);

  const fetchPermit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!id) {
        throw new Error('No permit ID provided');
      }
      
      const data = await permitService.getPermit(parseInt(id, 10));
      setPermit(data);
      
      // Initialize edit form with current permit data
      setEditForm({
        work_description: data.work_description || '',
        work_location: data.work_location || '',
        requested_start_time: data.requested_start_time ? new Date(data.requested_start_time).toISOString().slice(0, 16) : '',
        requested_end_time: data.requested_end_time ? new Date(data.requested_end_time).toISOString().slice(0, 16) : '',
        isolation_required: data.isolation_required || false,
        risk_assessment_completed: data.risk_assessment_completed || false,
        equipment_removed_from_service: data.equipment_removed_from_service || '',
        issuer_applied_safety_measures: data.issuer_applied_safety_measures || '',
        work_completion_notes: data.work_completion_notes || '',
        equipment_modifications_made: data.equipment_modifications_made || ''
      });
    } catch (err) {
      console.error('Error fetching permit:', err);
      setError('Failed to load permit details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch permit if authenticated and not loading auth state
    if (id && isAuthenticated && !authLoading) {
      fetchPermit();
    } else if (!authLoading && !isAuthenticated) {
      setError('Please log in to view permit details.');
      setLoading(false);
    }
  }, [id, isAuthenticated, authLoading]);

  // Fetch recipient options as user types
  useEffect(() => {
    const fetchRecipients = async () => {
      if (!permit?.site || !transferForm.recipientSearch) return;
      setTransferForm(f => ({ ...f, loadingRecipients: true }));
      try {
        const users: UserSearchResult = await authService.searchRecipients(permit.site.id, transferForm.recipientSearch);
        let options: any[] = [];
        if (Array.isArray(users)) {
          options = users;
        } else if (users && typeof users === 'object' && 'results' in users && Array.isArray((users as any).results)) {
          options = (users as { results: any[] }).results;
        }
        setTransferForm(f => ({ ...f, recipientOptions: options, loadingRecipients: false }));
      } catch {
        setTransferForm(f => ({ ...f, recipientOptions: [], loadingRecipients: false }));
      }
    };
    if (transferDialogOpen && transferForm.recipientSearch) {
      fetchRecipients();
    }
  }, [transferForm.recipientSearch, transferDialogOpen, permit?.site]);

  // Fetch recipient options as user types (reuse logic)
  useEffect(() => {
    const fetchRecipients = async () => {
      if (!permit?.site || !completeTransferForm.recipientSearch) return;
      setCompleteTransferForm(f => ({ ...f, loadingRecipients: true }));
      try {
        const users: UserSearchResult = await authService.searchRecipients(permit.site.id, completeTransferForm.recipientSearch);
        let options: any[] = [];
        if (Array.isArray(users)) {
          options = users;
        } else if (users && typeof users === 'object' && 'results' in users && Array.isArray((users as any).results)) {
          options = (users as { results: any[] }).results;
        }
        setCompleteTransferForm(f => ({ ...f, recipientOptions: options, loadingRecipients: false }));
      } catch {
        setCompleteTransferForm(f => ({ ...f, recipientOptions: [], loadingRecipients: false }));
      }
    };
    if (completeTransferDialogOpen && completeTransferForm.recipientSearch) {
      fetchRecipients();
    }
  }, [completeTransferForm.recipientSearch, completeTransferDialogOpen, permit?.site]);

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'planned': return 'default';
      case 'issued': return 'success';
      case 'suspended': return 'warning';
      case 'pending_cancellation': return 'info';
      case 'pending_transfer': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'planned': return <Assignment />;
      case 'issued': return <PlayArrow />;
      case 'suspended': return <Pause />;
      case 'pending_cancellation': return <Warning />;
      case 'pending_transfer': return <Warning />;
      case 'cancelled': return <Stop />;
      default: return <Assignment />;
    }
  };

  // Update getAvailableStatusTransitions to only show recipient actions for the actual recipient
  const getAvailableStatusTransitions = (currentStatus: string, userRole?: string) => {
    
    switch (currentStatus) {
      case 'planned':
        // Only issuers can issue or cancel planned permits
        if (userRole === 'issuer') {
          return [
            { value: 'issued', label: 'Issue Permit' },
            { value: 'cancelled', label: 'Cancel Permit' },
          ];
        }
        return [];
        
      case 'issued':
        // Recipients can request suspension or pending cancellation
        // Issuers can directly suspend or cancel
        if (
          userRole === 'recipient' &&
          user?.id && permit?.recipient?.id && user.id === permit.recipient.id
        ) {
          return [
            { value: 'suspended', label: 'Request Suspension' },
            { value: 'pending_cancellation', label: 'Return for Cancellation' },
            { value: 'pending_transfer', label: 'Return for Transfer' },
          ];
        } else if (userRole === 'issuer') {
          return [
            { value: 'suspended', label: 'Suspend Permit' },
            { value: 'cancelled', label: 'Cancel Permit' },
          ];
        }
        return [];
        
      case 'suspended':
        // Only issuers can re-issue or cancel suspended permits
        if (userRole === 'issuer') {
          return [
            { value: 'issued', label: 'Re-issue Permit' },
            { value: 'cancelled', label: 'Cancel Permit' },
          ];
        }
        return [];
        
      case 'pending_cancellation':
        // Only issuers can approve or reject pending cancellation requests
        if (userRole === 'issuer') {
          return [
            { value: 'cancelled', label: 'Approve Cancellation' },
            { value: 'issued', label: 'Reject Cancellation' },
          ];
        }
        return [];
        
      case 'cancelled':
        return []; // No transitions from cancelled - permit is closed
        
      default:
        return [];
    }
  };

  const updatePermitStatus = async () => {
    if (!permit || !newStatus || !statusReason.trim()) {
      return;
    }

    try {
      setUpdating(true);
      
      const updatedPermit = await permitService.updatePermitStatus(permit.id, newStatus, statusReason);
      setPermit(updatedPermit);
      setStatusDialogOpen(false);
      setNewStatus('');
      setStatusReason('');
      setMissingRequirements(null);
      setError(null);
      
              // TODO: Log status change to station log as per Re-GenOps requirements
      
    } catch (err) {
      console.error('Error updating permit status:', err);
      const apiError = err as ApiError;
      if (apiError?.data?.missing_requirements) {
        setMissingRequirements(apiError.data.missing_requirements);
        setError(apiError.data.error || 'Permit cannot be issued until requirements are satisfied.');
      } else {
        setError('Failed to update permit status. Please try again.');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleRequestCancellation = async () => {
    if (!permit) return;

    try {
      setRequestingCancellation(true);
      setError(null);
      
      // Adjust payload to match backend expectations
      const updatedPermit = await permitService.requestCancellation(permit.id, {
        workparty_signed_off: cancellationForm.workparty_signed_off,
        rasms_removed: cancellationForm.rasms_removed,
        restoration_checks_completed: cancellationForm.restoration_checks_completed,
        plant_restrictions: cancellationForm.plant_restrictions,
        plant_modifications: cancellationForm.plant_modifications
      });
      setPermit(updatedPermit);
      setCancellationDialogOpen(false);
      
      // Reset form
      setCancellationForm({
        workparty_signed_off: false,
        rasms_removed: false,
        restoration_checks_completed: false,
        plant_restrictions: '',
        plant_modifications: ''
      });
      
      setError(null);
      
    } catch (err) {
      console.error('Error requesting cancellation:', err);
      setError('Failed to request cancellation. Please try again.');
    } finally {
      setRequestingCancellation(false);
    }
  };

  const handleRequestTransfer = async () => {
    if (!permit) return;
    try {
      setRequestingTransfer(true);
      setError(null);
      const updatedPermit = await permitService.requestTransfer(permit.id, {
        new_recipient_id: transferForm.new_recipient?.id,
        transfer_notes: transferForm.transfer_notes,
        workparty_signed_off_for_transfer: transferForm.workparty_signed_off,
      });
      setPermit(updatedPermit);
      setTransferDialogOpen(false);
      setTransferForm({
        new_recipient: null,
        transfer_notes: '',
        workparty_signed_off: false,
        recipientOptions: [],
        recipientSearch: '',
        loadingRecipients: false,
      });
      setError(null);
    } catch (err) {
      setError('Failed to request transfer. Please try again.');
    } finally {
      setRequestingTransfer(false);
    }
  };

  const handleRequestSuspension = async () => {
    if (!permit) return;
    try {
      setRequestingSuspension(true);
      setError(null);
      const updatedPermit = await permitService.requestSuspension(permit.id, suspensionForm);
      setPermit(updatedPermit);
      setSuspensionDialogOpen(false);
      setSuspensionForm({
        suspension_rasm_removed: false,
        suspension_workparty_signed_off: false,
        suspension_modifications_notes: '',
        suspension_wip_updated: false,
        suspension_rasm_lock_removed: false,
      });
      setError(null);
    } catch (err) {
      setError('Failed to request suspension. Please try again.');
    } finally {
      setRequestingSuspension(false);
    }
  };

  const handleCompleteTransfer = async () => {
    if (!permit) return;
    try {
      setCompletingTransfer(true);
      setError(null);
      const updatedPermit = await permitService.completeTransfer(permit.id, {
        new_recipient_id: completeTransferForm.new_recipient?.id,
      });
      setPermit(updatedPermit);
      setCompleteTransferDialogOpen(false);
      setCompleteTransferForm({
        new_recipient: null,
        recipientOptions: [],
        recipientSearch: '',
        loadingRecipients: false,
      });
      setMissingRequirements(null);
      setError(null);
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError?.data?.missing_requirements) {
        setMissingRequirements(apiError.data.missing_requirements);
        setError(apiError.data.error || 'Recipient does not meet site safety requirements.');
      } else {
        setError('Failed to complete transfer. Please try again.');
      }
    } finally {
      setCompletingTransfer(false);
    }
  };

  const handleStatusChange = (transitionValue: string) => {
    // If this is a "Return for Cancellation" action, show the cancellation dialog
    if (transitionValue === 'pending_cancellation') {
      setCancellationDialogOpen(true);
    } else if (transitionValue === 'pending_transfer') {
      setTransferDialogOpen(true);
    } else if (transitionValue === 'suspended') {
      setSuspensionDialogOpen(true);
    } else {
      setNewStatus(transitionValue);
      setMissingRequirements(null);
      setStatusDialogOpen(true);
    }
  };

  const renderMissingRequirements = () => {
    if (!missingRequirements || missingRequirements.length === 0) {
      return null;
    }
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Missing requirements:
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {missingRequirements.map((item, index) => (
            <Box component="li" key={`${item.type}-${item.id}-${index}`}>
              {item.type === 'induction'
                ? `Induction: ${item.title}`
                : `Competency: ${item.code || ''} ${item.name || ''}`.trim()}
              {item.reason === 'expired' && item.expires_at && ` (expired ${item.expires_at})`}
              {item.reason === 'not_completed' && ' (not completed)'}
            </Box>
          ))}
        </Box>
      </Alert>
    );
  };

  const canEditPermit = (permit: Permit | null) => {
    if (!permit) return false;
    
    // Business rule: Only issuers can edit permits
    if (user?.role !== 'issuer') return false;
    
    // Can only edit permits that are:
    // 1. In 'planned' status (not yet issued) - can be edited by issuers
    // 2. In 'suspended' status (temporarily stopped for modifications) - can be edited by issuers
    // 3. Cannot edit 'issued' or 'cancelled' permits
    const editableStatuses = ['planned', 'suspended'];
    return editableStatuses.indexOf(permit.status) !== -1;
  };

  const handleEditToggle = () => {
    if (!permit) return;
    
    // Check if permit can be edited
    if (!canEditPermit(permit)) {
      setError('This permit cannot be edited. Only permits in "planned" or "suspended" status can be modified.');
      return;
    }
    
    if (editMode) {
      // Cancel editing - reset form to original values
      setEditForm({
        work_description: permit.work_description || '',
        work_location: permit.work_location || '',
        requested_start_time: permit.requested_start_time ? new Date(permit.requested_start_time).toISOString().slice(0, 16) : '',
        requested_end_time: permit.requested_end_time ? new Date(permit.requested_end_time).toISOString().slice(0, 16) : '',
        isolation_required: permit.isolation_required || false,
        risk_assessment_completed: permit.risk_assessment_completed || false,
        equipment_removed_from_service: permit.equipment_removed_from_service || '',
        issuer_applied_safety_measures: permit.issuer_applied_safety_measures || '',
        work_completion_notes: permit.work_completion_notes || '',
        equipment_modifications_made: permit.equipment_modifications_made || ''
      });
    }
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    if (!permit) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const updatedPermit = await permitService.updatePermit(permit.id, editForm);
      setPermit(updatedPermit);
      setEditMode(false);
      
      // Show success message
      setError(null);
    } catch (err) {
      console.error('Error updating permit:', err);
      setError('Failed to update permit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!permit) return;
    
    try {
      // Create a download link for the PDF
      const link = document.createElement('a');
      link.href = `/api/v1/permits/permits/${permit.id}/generate_pdf/`;
      link.download = `${permit.permit_number}_permit.pdf`;
      link.click();
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF. Please try again.');
    }
  };

  const handleViewPDF = () => {
    if (!permit) return;
    
    // Open PDF in new window
    window.open(`/api/v1/permits/permits/${permit.id}/view_pdf/`, '_blank');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (authLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
        <Typography variant="body2" ml={2}>
          {authLoading ? 'Checking authentication...' : 'Loading permit details...'}
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please log in to view permit details.
        </Alert>
        <Button onClick={() => navigate('/login')} variant="contained">
          Go to Login
        </Button>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate('/permits')} variant="outlined">
          Back to Permits
        </Button>
      </Box>
    );
  }

  // Early return if permit is null
  if (!permit) {
  return (
    <Box>
        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && <Alert severity="info">No permit found.</Alert>}
      </Box>
    );
  }

  const availableTransitions = getAvailableStatusTransitions(permit!.status, user?.role);

  // Helper: is user an issuer for the site?
  const isIssuerForSite = Boolean(
    user?.site_roles?.some(
      (r: any) =>
        r.site_id === permit?.site?.id &&
        r.role === 'issuer' &&
        r.is_active
    )
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Permit {permit!.permit_number || `PERMIT-${permit!.id}`}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {permit!.work_description}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/permits')}
            startIcon={<ArrowBack />}
          >
            Back to List
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/permits/${permit!.id}/edit`)}
          >
            Edit Permit
          </Button>
        </Stack>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {permit.status === 'pending_transfer' && isIssuerForSite && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            This permit is awaiting transfer completion. As an issuer, you can complete the transfer below.
          </Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              console.log('Complete Transfer clicked. Pending transfer to:', permit.pending_transfer_to);
              setCompleteTransferDialogOpen(true);
              
              // Ensure the pending transfer recipient is properly structured for display
              const proposedRecipient = permit.pending_transfer_to ? {
                ...permit.pending_transfer_to,
                full_name: permit.pending_transfer_to.full_name || 
                          (permit.pending_transfer_to.first_name && permit.pending_transfer_to.last_name 
                            ? `${permit.pending_transfer_to.first_name} ${permit.pending_transfer_to.last_name}` 
                            : permit.pending_transfer_to.username)
              } : null;
              
              setCompleteTransferForm(f => ({
                ...f,
                new_recipient: proposedRecipient,
                recipientOptions: proposedRecipient ? [proposedRecipient] : [],
                recipientSearch: '',
              }));
            }}
            sx={{ mb: 2 }}
          >
            Complete Transfer
          </Button>
        </>
      )}

      <Grid container spacing={3}>
        {/* Status Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
              <Box display="flex" alignItems="center" mb={2}>
                {getStatusIcon(permit!.status)}
                <Chip
                  label={permit!.status?.toUpperCase() || 'UNKNOWN'}
                  color={getStatusColor(permit!.status || 'planned')}
                  sx={{ ml: 1 }}
                />
              </Box>
              
              {availableTransitions.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => {
                    // If there's only one transition and it's "Return for Cancellation", show cancellation dialog
                    if (availableTransitions.length === 1 && availableTransitions[0].value === 'pending_cancellation') {
                      setCancellationDialogOpen(true);
                    } else {
                      setStatusDialogOpen(true);
                    }
                  }}
                  startIcon={<History />}
                >
                  Change Status
                </Button>
              )}

              {/* Debug Section - Remove after testing */}
              {process.env.NODE_ENV === 'development' && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    <strong>Debug Info:</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • User Role: <strong>{user?.role || 'Not logged in'}</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Permit Status: <strong>{permit!.status}</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Available Transitions: <strong>{availableTransitions.length}</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Permit Recipient: <strong>{permit!.recipient?.username}</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Current User: <strong>{user?.username || 'None'}</strong>
                  </Typography>
                  {availableTransitions.length > 0 && (
                    <Typography variant="body2" component="div">
                      • Available Actions: <strong>{availableTransitions.map(t => t.label).join(', ')}</strong>
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Permit Details */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Permit Details
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Site
                      </TableCell>
                      <TableCell>
                        {permit!.site ? `${permit!.site?.name} (${permit!.site?.code})` : 'No site assigned'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Location
                      </TableCell>
                      <TableCell>{permit!.work_location}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Recipient
                      </TableCell>
                      <TableCell>
                        {permit!.recipient?.first_name} {permit!.recipient?.last_name}
                        {permit!.recipient?.employee_id && ` (${permit!.recipient.employee_id})`}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Issuer
                      </TableCell>
                      <TableCell>
                        {permit!.issuer?.first_name} {permit!.issuer?.last_name}
                        {permit!.issuer?.employee_id && ` (${permit!.issuer.employee_id})`}
                      </TableCell>
                    </TableRow>
                    {permit!.supervisor && (
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Supervisor
                        </TableCell>
                        <TableCell>
                          {permit!.supervisor?.first_name} {permit!.supervisor?.last_name}
                          {permit!.supervisor?.employee_id && ` (${permit!.supervisor?.employee_id})`}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Start Time
                      </TableCell>
                      <TableCell>{formatDate(permit!.requested_start_time)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
                        End Time
                      </TableCell>
                      <TableCell>{formatDate(permit!.requested_end_time)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Created
                      </TableCell>
                      <TableCell>{formatDate(permit!.created_at)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Last Updated
                      </TableCell>
                      <TableCell>{permit!.updated_at ? formatDate(permit!.updated_at) : 'Unknown'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Work Description */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Work Description
              </Typography>
              <Typography variant="body1">
                {permit!.work_description || 'No description available'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Workflow Information */}
        {(permit!.status === 'pending_cancellation' || permit!.status === 'pending_transfer' || permit!.status === 'suspended') && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Workflow Information
                </Typography>
                
                {permit!.status === 'pending_cancellation' && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        This permit is pending cancellation. The recipient has completed the required checklist.
                      </Typography>
                    </Alert>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Work Party Signed Off
                            </TableCell>
                            <TableCell>
                              {permit!.workparty_signed_off ? '✓ Yes' : '✗ No'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              RASMs Removed
                            </TableCell>
                            <TableCell>
                              {permit!.rasms_removed ? '✓ Yes' : '✗ No'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Restoration Checks Completed
                            </TableCell>
                            <TableCell>
                              {permit!.restoration_checks_completed ? '✓ Yes' : '✗ No'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Plant Restrictions
                            </TableCell>
                            <TableCell>
                              {permit!.plant_restrictions || 'None specified'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Plant Modifications
                            </TableCell>
                            <TableCell>
                              {permit!.plant_modifications || 'None specified'}
                            </TableCell>
                          </TableRow>
                          {permit!.cancellation_requested_at && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Cancellation Requested
                              </TableCell>
                              <TableCell>
                                {formatDate(permit!.cancellation_requested_at)}
                                {permit!.cancellation_requested_by && ` by ${permit!.cancellation_requested_by.full_name}`}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {permit!.status === 'pending_transfer' && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        This permit is pending transfer to a new recipient.
                      </Typography>
                    </Alert>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Proposed New Recipient
                            </TableCell>
                            <TableCell>
                              {permit!.pending_transfer_to ? 
                                `${permit!.pending_transfer_to.full_name} (${permit!.pending_transfer_to.employee_id})` : 
                                'No recipient selected'
                              }
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Work Party Signed Off for Transfer
                            </TableCell>
                            <TableCell>
                              {permit!.workparty_signed_off_for_transfer ? '✓ Yes' : '✗ No'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Transfer Notes
                            </TableCell>
                            <TableCell>
                              {permit!.transfer_notes || 'No notes provided'}
                            </TableCell>
                          </TableRow>
                          {permit!.transfer_completed_at && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Transfer Completed
                              </TableCell>
                              <TableCell>
                                {formatDate(permit!.transfer_completed_at)}
                                {permit!.transfer_issuer && ` by ${permit!.transfer_issuer.full_name}`}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {permit!.status === 'suspended' && (
                  <Box>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        This permit has been suspended. The recipient has completed the required suspension checklist.
                      </Typography>
                    </Alert>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              RASMs Removed for Suspension
                            </TableCell>
                            <TableCell>
                              {permit!.suspension_rasm_removed ? '✓ Yes' : '✗ No'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Work Party Signed Off for Suspension
                            </TableCell>
                            <TableCell>
                              {permit!.suspension_workparty_signed_off ? '✓ Yes' : '✗ No'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Modifications/Equipment Status
                            </TableCell>
                            <TableCell>
                              {permit!.suspension_modifications_notes || 'No notes provided'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              WIP Board Updated
                            </TableCell>
                            <TableCell>
                              {permit!.suspension_wip_updated ? '✓ Yes' : '✗ No'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              RASM Lock Removed
                            </TableCell>
                            <TableCell>
                              {permit!.suspension_rasm_lock_removed ? '✓ Yes' : '✗ No'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Permit Status</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Status: <strong>{permit!.status?.toUpperCase() || 'UNKNOWN'}</strong>
            </Typography>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                label="New Status"
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {availableTransitions.map(transition => (
                  <MenuItem key={transition.value} value={transition.value}>
                    {transition.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Reason for Status Change"
              multiline
              rows={3}
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              margin="normal"
              helperText="Please provide a reason for this status change"
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Change status from <strong>{permit!.status}</strong> to <strong>{newStatus}</strong>
              </Typography>
            </Alert>
            {renderMissingRequirements()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setStatusDialogOpen(false); setMissingRequirements(null); }}>
            Cancel
          </Button>
          <Button
            onClick={updatePermitStatus}
            variant="contained"
            disabled={!newStatus || !statusReason.trim() || updating}
          >
            {updating ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation Request Dialog */}
      <Dialog
        open={cancellationDialogOpen}
        onClose={() => setCancellationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Return Permit for Cancellation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Please confirm the following before returning the permit for cancellation.
              </Typography>
            </Alert>
            
            <FormControlLabel
              control={
                <Switch
                  checked={cancellationForm.workparty_signed_off}
                  onChange={(e) => setCancellationForm({
                    ...cancellationForm,
                    workparty_signed_off: e.target.checked
                  })}
                />
              }
              label="Work party is signed off"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={cancellationForm.rasms_removed}
                  onChange={(e) => setCancellationForm({
                    ...cancellationForm,
                    rasms_removed: e.target.checked
                  })}
                />
              }
              label="All RASMs are removed"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={cancellationForm.restoration_checks_completed}
                  onChange={(e) => setCancellationForm({
                    ...cancellationForm,
                    restoration_checks_completed: e.target.checked
                  })}
                />
              }
              label="All restoration checks are completed"
            />
            <Divider sx={{ my: 2 }} />
            <TextField
              fullWidth
              label="Restrictions or limitations for the plant"
              multiline
              rows={2}
              value={cancellationForm.plant_restrictions}
              onChange={(e) => setCancellationForm({
                ...cancellationForm,
                plant_restrictions: e.target.value
              })}
              margin="normal"
              helperText="Advise any restrictions or limitations for the plant"
            />
            <TextField
              fullWidth
              label="Modifications to the plant"
              multiline
              rows={2}
              value={cancellationForm.plant_modifications}
              onChange={(e) => setCancellationForm({
                ...cancellationForm,
                plant_modifications: e.target.value
              })}
              margin="normal"
              helperText="Advise any modifications to the plant"
            />
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                All items must be completed before the permit can be returned for cancellation.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancellationDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleRequestCancellation}
            variant="contained"
            disabled={
              !cancellationForm.workparty_signed_off ||
              !cancellationForm.rasms_removed ||
              !cancellationForm.restoration_checks_completed ||
              !cancellationForm.plant_restrictions.trim() ||
              !cancellationForm.plant_modifications.trim() ||
              requestingCancellation
            }
          >
            {requestingCancellation ? 'Requesting Cancellation...' : 'Request Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Request Dialog */}
      <Dialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Return Permit for Transfer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Please select the new recipient (optional), add any transfer notes, and confirm the work party has been advised and signed off the permit.
              </Typography>
            </Alert>
            <Autocomplete
              options={transferForm.recipientOptions}
              getOptionLabel={option => option.full_name || option.username || ''}
              loading={transferForm.loadingRecipients}
              value={transferForm.new_recipient}
              onChange={(_, value) => setTransferForm(f => ({ ...f, new_recipient: value }))}
              onInputChange={(_, value) => setTransferForm(f => ({ ...f, recipientSearch: value }))}
              renderInput={params => (
                <TextField {...params} label="New Recipient (optional)" margin="normal" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <TextField
              fullWidth
              label="Transfer Notes (optional)"
              multiline
              rows={2}
              value={transferForm.transfer_notes}
              onChange={e => setTransferForm(f => ({ ...f, transfer_notes: e.target.value }))}
              margin="normal"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={transferForm.workparty_signed_off}
                  onChange={e => setTransferForm(f => ({ ...f, workparty_signed_off: e.target.checked }))}
                />
              }
              label="Work party has been advised and signed off the permit"
            />
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                You must confirm the work party has been advised and signed off before submitting the transfer.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRequestTransfer}
            variant="contained"
            disabled={!transferForm.workparty_signed_off || requestingTransfer}
          >
            {requestingTransfer ? 'Requesting Transfer...' : 'Request Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspension Request Dialog */}
      <Dialog
        open={suspensionDialogOpen}
        onClose={() => setSuspensionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Return Permit for Suspension</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Please complete the following checklist before requesting suspension.
              </Typography>
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={suspensionForm.suspension_rasm_removed}
                  onChange={e => setSuspensionForm(f => ({ ...f, suspension_rasm_removed: e.target.checked }))}
                />
              }
              label="RASM's have been removed"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={suspensionForm.suspension_workparty_signed_off}
                  onChange={e => setSuspensionForm(f => ({ ...f, suspension_workparty_signed_off: e.target.checked }))}
                />
              }
              label="Work party has signed off and supervisors have removed RASM locks"
            />
            <TextField
              fullWidth
              label="Status of modifications and equipment"
              multiline
              rows={2}
              value={suspensionForm.suspension_modifications_notes}
              onChange={e => setSuspensionForm(f => ({ ...f, suspension_modifications_notes: e.target.value }))}
              margin="normal"
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={suspensionForm.suspension_wip_updated}
                  onChange={e => setSuspensionForm(f => ({ ...f, suspension_wip_updated: e.target.checked }))}
                />
              }
              label="WIP board has been updated"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={suspensionForm.suspension_rasm_lock_removed}
                  onChange={e => setSuspensionForm(f => ({ ...f, suspension_rasm_lock_removed: e.target.checked }))}
                />
              }
              label="RASM lock has been removed from lockbox"
            />
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                All checklist items must be completed and notes provided before submitting the suspension.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspensionDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRequestSuspension}
            variant="contained"
            disabled={
              !suspensionForm.suspension_rasm_removed ||
              !suspensionForm.suspension_workparty_signed_off ||
              !suspensionForm.suspension_modifications_notes.trim() ||
              !suspensionForm.suspension_wip_updated ||
              !suspensionForm.suspension_rasm_lock_removed ||
              requestingSuspension
            }
          >
            {requestingSuspension ? 'Requesting Suspension...' : 'Request Suspension'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Transfer Dialog */}
      <Dialog
        open={completeTransferDialogOpen}
        onClose={() => { setCompleteTransferDialogOpen(false); setMissingRequirements(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Complete Permit Transfer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {permit?.pending_transfer_to 
                  ? `The original recipient has proposed transferring this permit to: ${permit.pending_transfer_to.full_name || permit.pending_transfer_to.username}. You can confirm this selection or choose a different recipient.`
                  : 'Confirm or change the new recipient for this permit transfer.'
                }
              </Typography>
            </Alert>
            {process.env.NODE_ENV === 'development' && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  <strong>Debug Info:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • Pending Transfer To: <strong>{permit?.pending_transfer_to?.full_name || 'None'}</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • Form New Recipient: <strong>{completeTransferForm.new_recipient?.full_name || 'None'}</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • Recipient Options Count: <strong>{completeTransferForm.recipientOptions.length}</strong>
                </Typography>
              </Box>
            )}
            <Autocomplete
              options={completeTransferForm.recipientOptions}
              getOptionLabel={option => {
                // More robust option label generation
                if (option.full_name) return option.full_name;
                if (option.first_name && option.last_name) return `${option.first_name} ${option.last_name}`;
                if (option.username) return option.username;
                return `User ${option.id}`;
              }}
              loading={completeTransferForm.loadingRecipients}
              value={completeTransferForm.new_recipient}
              onChange={(_, value) => setCompleteTransferForm(f => ({ ...f, new_recipient: value }))}
              onInputChange={(_, value) => setCompleteTransferForm(f => ({ ...f, recipientSearch: value }))}
              renderInput={params => (
                <TextField {...params} label="New Recipient" margin="normal" />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body1">
                      {option.full_name || `${option.first_name} ${option.last_name}` || option.username}
                    </Typography>
                    {option.employee_id && (
                      <Typography variant="body2" color="textSecondary">
                        ID: {option.employee_id}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
              filterOptions={(options, { inputValue }) => {
                // If we have a pre-selected value that's not in options, include it
                const filtered = options.filter(option => {
                  const searchText = inputValue.toLowerCase();
                  return (
                    option.full_name?.toLowerCase().includes(searchText) ||
                    option.first_name?.toLowerCase().includes(searchText) ||
                    option.last_name?.toLowerCase().includes(searchText) ||
                    option.username?.toLowerCase().includes(searchText) ||
                    option.employee_id?.toLowerCase().includes(searchText)
                  );
                });
                
                // If we have a value but it's not in the filtered results, add it
                if (completeTransferForm.new_recipient && 
                    !filtered.some(option => option.id === completeTransferForm.new_recipient.id)) {
                  filtered.unshift(completeTransferForm.new_recipient);
                }
                
                return filtered;
              }}
            />
            {renderMissingRequirements()}
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                You must select a recipient to complete the transfer.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteTransferDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCompleteTransfer}
            variant="contained"
            disabled={!completeTransferForm.new_recipient || completingTransfer}
          >
            {completingTransfer ? 'Completing Transfer...' : 'Complete Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermitDetail; 